import { z } from "zod";
import { createFiniteDecimalFromAnyString } from "@goodfill/decimal";
import Decimal from "decimal.js";

const FED_BALANCE_SHEET_AS_OF_MATCH_2023 = 8_310_000_000_000;

const TOO_BIG_NUMBER = new Decimal(FED_BALANCE_SHEET_AS_OF_MATCH_2023).mul(1_000_000);

export interface zDecimalOptions {
  errorMessages?: { invalid?: string; tooBig?: string };
  maxDecimal?: Decimal;
  scientific?: boolean;
}

const DEFAULT_OPTIONS = {
  errorMessages: {
    invalid: "'[--number--6633d529d1]' is not valid number",
    tooBig: "'[--number--6633d529d1]' is insanely big",
  },
  maxDecimal: TOO_BIG_NUMBER,
  scientific: false,
};

export const zDecimal = (options?: zDecimalOptions) =>
  z.string().transform((value, ctx) => {
    const {
      errorMessages: { invalid, tooBig },
      maxDecimal,
      scientific,
    } = {
      errorMessages: {
        invalid: options?.errorMessages?.invalid ?? DEFAULT_OPTIONS.errorMessages.invalid,
        tooBig: options?.errorMessages?.tooBig ?? DEFAULT_OPTIONS.errorMessages.tooBig,
      },
      maxDecimal: options?.maxDecimal ?? DEFAULT_OPTIONS.maxDecimal,
      scientific: options?.scientific ?? DEFAULT_OPTIONS.scientific,
    };

    if (!scientific && value.toLowerCase().includes("e")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Scientific format is not allowed.",
      });

      return z.NEVER;
    }

    try {
      const decimalValue = createFiniteDecimalFromAnyString(value, "");

      if (decimalValue.greaterThan(maxDecimal)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tooBig.replace("[--number--6633d529d1]", value),
        });

        return z.NEVER;
      }

      return decimalValue;
    } catch (e) {
      if (e instanceof Error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: invalid.replace("[--number--6633d529d1]", value),
        });

        return z.NEVER;
      }

      throw e;
    }
  });
